/**
 * Crisp Chat Integration
 * Provides in-app customer support chat with full user context
 * Crisp is lazy-loaded only when user requests support - no flash on page load
 */

import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

// Crisp Website ID
const CRISP_WEBSITE_ID = 'b760be24-e296-4f9e-84d8-a76ecb7aa7df';

// Track if Crisp has been loaded
let crispLoaded = false;
let crispLoading = false;

// Extend window type for Crisp
declare global {
  interface Window {
    $crisp: any[];
    CRISP_WEBSITE_ID: string;
  }
}

/**
 * Loads Crisp script and sets up user data
 * Only called once when user first requests support
 */
async function loadCrisp(): Promise<void> {
  if (crispLoaded || crispLoading) return;
  crispLoading = true;

  // Initialize Crisp
  window.$crisp = [];
  window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

  // Position on left side to avoid overlap with AI chat button (bottom-right)
  window.$crisp.push(['config', 'position:reverse', true]);

  // Set TrainerPulse branding color
  window.$crisp.push(['config', 'color:theme', 'green']);

  // Load Crisp script
  const script = document.createElement('script');
  script.src = 'https://client.crisp.chat/l.js';
  script.async = true;
  document.head.appendChild(script);

  // Set user data once Crisp is ready
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      crispLoaded = true;
      return;
    }

    // Get profile and subscription data for full context
    const [profileResult, subscriptionResult] = await Promise.all([
      supabase
        .from('profiles')
        .select('first_name, last_name, created_at')
        .eq('id', user.id)
        .single(),
      supabase
        .from('subscriptions')
        .select('tier, status, trial_ends_at')
        .eq('user_id', user.id)
        .single(),
    ]);

    const profile = profileResult.data;
    const subscription = subscriptionResult.data;

    // Wait for Crisp to be ready
    const waitForCrisp = setInterval(() => {
      if (window.$crisp && typeof window.$crisp.push === 'function') {
        clearInterval(waitForCrisp);
        crispLoaded = true;

        // Set user email
        if (user.email) {
          window.$crisp.push(['set', 'user:email', user.email]);
        }

        // Set user name
        if (profile?.first_name || profile?.last_name) {
          const name = [profile.first_name, profile.last_name]
            .filter(Boolean)
            .join(' ');
          if (name) {
            window.$crisp.push(['set', 'user:nickname', name]);
          }
        }

        // Set comprehensive session data for support context
        const sessionData: [string, string][] = [
          ['user_id', user.id],
          ['app', 'TrainerPulse'],
          ['plan', subscription?.tier || 'starter'],
          ['status', subscription?.status || 'unknown'],
        ];

        // Add account age
        if (profile?.created_at) {
          const createdDate = new Date(profile.created_at);
          const daysOld = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
          sessionData.push(['account_age_days', String(daysOld)]);
        }

        // Add trial info if applicable
        if (subscription?.trial_ends_at) {
          const trialEnd = new Date(subscription.trial_ends_at);
          const daysLeft = Math.ceil((trialEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
          if (daysLeft > 0) {
            sessionData.push(['trial_days_left', String(daysLeft)]);
          }
        }

        window.$crisp.push(['set', 'session:data', sessionData]);

        // Add segment based on plan for routing/prioritization
        if (subscription?.tier) {
          const tierSegments: Record<string, string> = {
            'starter': 'Starter Plan',
            'pro': 'Pro Plan',
            'elite': 'Elite Plan',
            'team_pro': 'Team Pro',
            'team_elite': 'Team Elite',
          };
          const segment = tierSegments[subscription.tier] || 'Unknown Plan';
          window.$crisp.push(['set', 'session:segments', [[segment]]]);
        }

        // Now open the chat
        window.$crisp.push(['do', 'chat:show']);
        window.$crisp.push(['do', 'chat:open']);
      }
    }, 100);

    // Clear interval after 10 seconds if Crisp doesn't load
    setTimeout(() => {
      clearInterval(waitForCrisp);
      crispLoaded = true;
    }, 10000);
  } catch (error) {
    console.error('Error setting Crisp user data:', error);
    crispLoaded = true;
  }
}

/**
 * Opens the Crisp support chat widget
 * Lazy-loads Crisp on first call to prevent any flash on page load
 */
export function openSupportChat() {
  toast({
    title: "Connecting you to support",
    description: "A team member will respond shortly.",
  });

  if (crispLoaded && window.$crisp) {
    // Crisp already loaded, just open it
    window.$crisp.push(['do', 'chat:show']);
    window.$crisp.push(['do', 'chat:open']);
  } else {
    // Load Crisp for the first time (will auto-open when ready)
    loadCrisp();
  }
}

/**
 * CrispChat component - now a no-op since we lazy-load
 * Kept for backwards compatibility with Layout.tsx
 */
export function CrispChat() {
  // Crisp is now lazy-loaded only when user requests support
  // No useEffect, no script loading on mount = no flash
  return null;
}
